import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";

import { faUsers } from "@fortawesome/pro-solid-svg-icons/faUsers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";

import EmailTemplate from "@mapmycustomers/shared/types/email/EmailTemplate";
import User from "@mapmycustomers/shared/types/User";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { userDisplayName } from "@app/util/formatters";

import messages from "./messages";
import styles from "./TemplateUsers.module.scss";

const PREVIEW_USERS_COUNT = 3;

interface TemplateUsersProps {
  template: EmailTemplate;
  users: User[];
}

const TemplateUsers: React.FC<TemplateUsersProps> = ({ template, users }) => {
  const intl = useIntl();

  const templateUsers = useMemo(() => {
    return users.filter((user) => template.userIds.includes(user.id));
  }, [template.userIds, users]);

  const expandable = templateUsers.length > PREVIEW_USERS_COUNT;

  const [expanded, expand, collapse] = useBoolean(false);

  const label = useMemo(
    () =>
      (expanded ? templateUsers : templateUsers.slice(0, PREVIEW_USERS_COUNT))
        .map(userDisplayName)
        .join(", "),

    [expanded, templateUsers]
  );

  useEffect(() => {
    if (expandable) {
      collapse();
    }
  }, [expandable, collapse, template]);

  if (!templateUsers.length) {
    return null;
  }

  return (
    <div className={styles.container}>
      <FontAwesomeIcon className={styles.icon} icon={faUsers} />
      <div className={styles.content}>
        <span className={styles.label}>
          {intl.formatMessage(messages.templateUsersLabel, { text: label })}
        </span>
        {expandable &&
          (expanded ? (
            <Button onClick={collapse} size="small" type="link">
              {intl.formatMessage(messages.lessTemplateUsersButtonLabel)}
            </Button>
          ) : (
            <>
              ,
              <Button onClick={expand} size="small" type="link">
                {intl.formatMessage(messages.moreTemplateUsersButtonLabel, {
                  count: templateUsers.length - PREVIEW_USERS_COUNT,
                })}
              </Button>
            </>
          ))}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  users: getUsers(state),
});

export default connect(mapStateToProps)(TemplateUsers);
